import {useAlive} from '@github-ui/use-alive'
import useIsMounted from '@github-ui/use-is-mounted'
import {throttle} from '@github/mini-throttle'
import {useCallback, useEffect, useMemo, useSyncExternalStore} from 'react'

const FOREGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS = 2000
const BACKGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS = 10000

function subscribeToVisibility(notify: () => void) {
  document.addEventListener('visibilitychange', notify)
  return () => {
    document.removeEventListener('visibilitychange', notify)
  }
}

function getVisibilitySnapshot() {
  return !document.hidden
}

function getVisibilityServerSnapshot() {
  return true
}

interface UseChecksLiveUpdatesOptions {
  refetch: () => void
  aliveChannel?: string | null
  isActive?: boolean
}

export function useChecksLiveUpdates({refetch, aliveChannel, isActive}: UseChecksLiveUpdatesOptions) {
  const isMounted = useIsMounted()
  const isPageVisible = useSyncExternalStore(subscribeToVisibility, getVisibilitySnapshot, getVisibilityServerSnapshot)
  const throttleTiming = isPageVisible
    ? FOREGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS
    : BACKGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS

  const throttledRefetch = useMemo(
    () =>
      throttle(() => {
        if (isMounted()) {
          void refetch()
        }
      }, throttleTiming),
    [isMounted, refetch, throttleTiming],
  )

  useEffect(() => {
    return () => {
      throttledRefetch.cancel()
    }
  }, [throttledRefetch])

  const handleNotification = useCallback(
    (data: {wait?: number}) => {
      window.setTimeout(throttledRefetch, data.wait || 0)
    },
    [throttledRefetch],
  )

  useAlive(isActive && aliveChannel ? aliveChannel : null, handleNotification)
}
